import { ColDef } from "ag-grid-community";

import { Architecture, BomOutput, Project } from "fond/types";
import { feetToMetersDisplay, formatUnit, isUnitConversionRequired, metersToFeetDisplay } from "fond/utils";
import numberValueFormatter from "fond/widgets/AgGrid/valueFormatter/number";

import { parseTags, UNTAGGED } from "./tags";

export interface BomModalProps {
  title?: string;
  project: Project;
  architecture: Architecture | null;
  onClose(): void;
}

export interface BomTreeRowData {
  path: string[];
  name: string;
  description: string | null;
  unit: string | null;
  count: number | null;
  unitCost: number | null;
  total: number;
  tag: string;
  group: string;
  // Allow dynamic partition fields like "partition1_count", "partition1_total", etc.
  [key: string]: string | string[] | number | null;
}

export const columnDefs: ColDef<BomTreeRowData>[] = [
  {
    field: "description",
    headerName: "Description",
    minWidth: 250,
    wrapText: true,
    autoHeight: true,
  },
  {
    field: "unit",
    headerName: "Unit",
    minWidth: 75,
  },
  {
    field: "count",
    headerName: "Quantity",
    minWidth: 75,
    type: "numericColumn",
    valueFormatter: numberValueFormatter,
  },
  {
    field: "unitCost",
    headerName: "Unit cost",
    minWidth: 75,
    type: "numericColumn",
    valueFormatter: numberValueFormatter,
  },
  {
    field: "total",
    headerName: "Total cost",
    minWidth: 75,
    type: "numericColumn",
    aggFunc: "sum",
    valueFormatter: numberValueFormatter,
    pinned: "right",
  },
];

export function flattenBomData(bom: BomOutput, systemOfMeasurement: string): BomTreeRowData[] {
  const rows: BomTreeRowData[] = [];

  for (const group of bom.groups) {
    for (const rule of group.ruleOutput) {
      const count = Number(rule.count);

      // Filter out zero-count rules.
      if (count <= 0) continue;

      const unitConversionRequired = isUnitConversionRequired(rule.unit, systemOfMeasurement);
      const parsedTags = parseTags(rule.tags);
      const tag = parsedTags.length ? parsedTags.join(", ") : UNTAGGED;
      const row: BomTreeRowData = {
        path: [group.name, rule.id],
        name: rule.id,
        description: rule.description,
        unit: unitConversionRequired ? formatUnit(systemOfMeasurement) : rule.unit,
        // If the BOM rule is in meters but the system of measurement is imperial, then we want to update
        // the count to be in feet (instead of meters) for display purposes. This is achieved using the
        // `metersToFeetDisplay` function below, which multiples the count by approximately 3.
        //
        // The total cost should remain the same no matter what unit system we're measuring length with,
        // and the total cost is calculated as count * unitCost. This means we need to multiply the unit cost
        // by approximately 1/3, i.e. we need to use `feetToMetersDisplay`.
        count: unitConversionRequired ? metersToFeetDisplay(count) : count,
        unitCost: unitConversionRequired ? feetToMetersDisplay(Number(rule.unitCost)) : Number(rule.unitCost),
        total: Number(rule.total),
        tag,
        group: group.name,
      };

      if ("partitions" in rule) {
        for (const partition of rule.partitions) {
          const columnPrefix = `${partition.name}_`;
          const partitionCount = Number(partition.count);

          row[`${columnPrefix}count`] = unitConversionRequired ? metersToFeetDisplay(partitionCount) : partitionCount;
          row[`${columnPrefix}total`] = Number(partition.total);
        }
      }

      rows.push(row);
    }
  }

  return rows;
}
